/** @module Find a card by name or "JTG 185" when none of the suggestions is the right one. */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { useAuthedImage } from "@/lib/images";

type Card = components["schemas"]["CardPrintOut"];

/** Waiting this long after the last key press keeps a fast typist to one request. */
const DEBOUNCE_MS = 350;
const MIN_LENGTH = 2;
const LIST_LIMIT = 12;
const GRID_LIMIT = 30;

export async function searchCards(query: string, limit = 12): Promise<Card[]> {
  const { data, response } = await api.GET("/api/v1/cards/search", {
    params: { query: { q: query, limit } },
  });
  if (!data) throw problemFrom(response);
  return data.items;
}

/** The text to start the search with: set code or set size narrow the read number down. */
export function startingQuery(
  ocr: { number?: string; set_code?: string; total?: string } | undefined,
): string {
  if (!ocr?.number) return "";
  if (ocr.set_code) return `${ocr.set_code} ${ocr.number}`;
  return ocr.total ? `${ocr.number}/${ocr.total}` : ocr.number;
}

function subtitle(card: Card): string {
  const parts = [card.set_name ?? card.set_id, card.local_id];
  if (card.set_released) parts.push(card.set_released.slice(0, 4));
  if (card.rarity) parts.push(card.rarity);
  return parts.join(" · ");
}

function Result({ card, onPick }: { card: Card; onPick: () => void }) {
  const url = useAuthedImage(`/api/v1/images/cards/${card.id}/low`);
  return (
    <button
      type="button"
      onClick={onPick}
      className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] border border-(--border) p-2 text-left"
    >
      <span className="h-16 w-12 shrink-0">
        {url ? (
          <img
            src={url}
            alt=""
            className="h-full w-full rounded-[var(--radius-sm)] object-contain"
          />
        ) : (
          <span className="block h-full w-full rounded-[var(--radius-sm)] bg-(--surface-2)" />
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[length:var(--text-sm)]">{card.name}</span>
        <span className="truncate text-[length:var(--text-xs)] text-(--text-muted)">
          {subtitle(card)}
        </span>
      </span>
    </button>
  );
}

/** The same card as a tile: the picture decides, the text only confirms. */
function Tile({ card, onPick }: { card: Card; onPick: () => void }) {
  const url = useAuthedImage(`/api/v1/images/cards/${card.id}/low`);
  return (
    <button
      type="button"
      onClick={onPick}
      title={`${card.name} · ${subtitle(card)}`}
      className="flex w-full flex-col gap-1 rounded-[var(--radius-sm)] border border-(--border) p-2 text-left hover:border-(--accent)"
    >
      <span className="aspect-[63/88] w-full">
        {url ? (
          <img
            src={url}
            alt=""
            className="h-full w-full rounded-[var(--radius-sm)] object-contain"
          />
        ) : (
          <span className="block h-full w-full rounded-[var(--radius-sm)] bg-(--surface-2)" />
        )}
      </span>
      <span className="truncate text-[length:var(--text-sm)]">{card.name}</span>
      <span className="truncate text-[length:var(--text-xs)] text-(--text-muted)">
        {subtitle(card)}
      </span>
    </button>
  );
}

export function CardPicker({
  initialQuery,
  onPick,
  layout = "list",
}: {
  initialQuery: string;
  onPick: (printId: string) => void;
  /** Tiles where there is room to judge by the picture; a list in the narrow column. */
  layout?: "list" | "grid";
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [term, setTerm] = useState(initialQuery.trim());

  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(query.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  // Tiles are the place to browse, so they are worth more results.
  const limit = layout === "grid" ? GRID_LIMIT : LIST_LIMIT;
  const results = useQuery({
    queryKey: ["card-search", term, limit],
    queryFn: () => searchCards(term, limit),
    enabled: term.length >= MIN_LENGTH,
  });

  return (
    <div className="flex flex-col gap-2" data-testid="card-picker">
      <input
        type="search"
        value={query}
        ref={(node) => node?.focus()}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        placeholder={t("review.searchPlaceholder")}
        aria-label={t("review.searchLabel")}
        className="min-h-(--touch) rounded-[var(--radius-sm)] border border-(--border-strong) px-3"
      />
      {results.isError ? (
        <p role="alert" className="text-[length:var(--text-sm)] text-(--loss)">
          {t("common.loadError")}
        </p>
      ) : null}
      {results.data?.length === 0 ? (
        <p className="text-[length:var(--text-sm)] text-(--text-muted)">{t("review.noResults")}</p>
      ) : null}
      {results.isPending && term.length >= MIN_LENGTH ? (
        <p className="text-[length:var(--text-sm)] text-(--text-muted)">{t("common.loading")}</p>
      ) : null}
      {layout === "grid" ? (
        <ul className="grid max-h-[46vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4 lg:grid-cols-6">
          {(results.data ?? []).map((card) => (
            <li key={card.id}>
              <Tile
                card={card}
                onPick={() => {
                  onPick(card.id);
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {(results.data ?? []).map((card) => (
            <li key={card.id}>
              <Result
                card={card}
                onPick={() => {
                  onPick(card.id);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
